import { useEffect, useState } from "react";
import { useStateValue } from "../../context/StateProvider";
import { formatNumber } from "../../utils/helper";
import { commissionForAgent, dealCreditsAgent } from "../../utils/dealCredit";
import YearFilterDropdown from "./YearFilterDropdown";
import StatDetails from "./../deals/StatDetails";

function TeamPerformanceStats({ id, childSelectedYear, isWidth = true }) {
  const [{ users, deals }] = useStateValue();

  const [selectedMonth, setSelectedMonth] = useState(null);
  const [statspopup, setstatspopup] = useState(false);
  const [selectedstat, setselectedstat] = useState("");
  const [statsdata, setstatsdata] = useState([]);

  const currentYear = new Date().getFullYear();

  const currentMonth = new Date().getMonth() + 1;
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const yearsList = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const [filterYear, setFilterYear] = useState(String(currentYear));
  const [yearDropdownVisible, setYearDropdownVisible] = useState(false);

  useEffect(() => {
    setSelectedMonth((prev) =>
      prev
        ? { month: prev.month, year: Number(filterYear) }
        : { month: currentMonth, year: Number(filterYear) },
    );
    childSelectedYear(selectedYear);
  }, [selectedYear]);

  // Find selected user
  const selectedUser = users.find((u) => String(u.id) === String(id));
  if (!selectedUser) return <div>User not found</div>;

  const { firstname, lastname, role } = selectedUser;

  // Units and volume belong to exactly one person per deal.
  const creditsThisUser = (d) => dealCreditsAgent(d, selectedUser.id);
  // Dollars follow whoever was actually paid: the agent's slice, the team
  // lead's slice, or both when the same person filled both roles.
  const commissionFor = (d) => commissionForAgent(d, selectedUser.id);

  // All deals this person is on: credited to them, or simply paying them a
  // slice. Role plays no part — an Agent can be the team lead of record on a
  // deal, and he still has to be paid for it.
  const matchedDeals = deals.filter(
    (deal) => creditsThisUser(deal) || commissionFor(deal) > 0,
  );

  const months = Array.from({ length: 12 }, (_, i) => ({
    month: i + 1,
    year: selectedYear,
  }));

  // Build monthly data
  const monthlyData = months.map(({ month, year }) => {
    const monthlyDeals = matchedDeals.filter((d) => {
      const dd = new Date(d.closingdate);
      return dd.getFullYear() === year && dd.getMonth() + 1 === month;
    });

    const monthlyDealsPending = matchedDeals.filter((d) => {
      const dd = new Date(d.closingdate);
      return dd.getFullYear() === year && dd.getMonth() + 1 === month;
    });

    const closed = monthlyDeals
      .filter((d) => d.dealStage.toLowerCase() === "closed")
      .reduce((sum, d) => sum + commissionFor(d), 0);

    const pending = monthlyDealsPending
      .filter(
        (d) =>
          !["closed", "cancelled", "expired"].includes(
            d.dealStage.toLowerCase(),
          ),
      )
      .reduce((sum, d) => sum + commissionFor(d), 0);

    const closedDealsCount = monthlyDeals.filter(
      (d) => d.dealStage.toLowerCase() === "closed" && creditsThisUser(d),
    ).length;

    return {
      month,
      year,
      closedCommission: closed,
      pendingCommission: pending,
      netCommission: closed + pending,
      closedDealsCount,
    };
  });

  // Current year totals
  const closedDealsCount = matchedDeals.filter(
    (d) =>
      d.dealStage.toLowerCase() === "closed" &&
      new Date(d.closingdate).getFullYear() === selectedYear &&
      creditsThisUser(d),
  );

  const ytdVolume = matchedDeals
    .filter(
      (d) =>
        d.dealStage.toLowerCase() === "closed" &&
        new Date(d.closingdate).getFullYear() === selectedYear &&
        creditsThisUser(d),
    )
    .reduce((sum, d) => sum + Number(d.offerPrice || 0), 0);

  const pendingDealsYTD = matchedDeals.filter((d) => {
    if (!d.closingdate) return false;

    const dealYear = new Date(d.closingdate).getFullYear();
    if (dealYear !== selectedYear) return false;

    const stage = d.dealStage?.toLowerCase();
    if (["closed", "cancelled", "expired"].includes(stage)) return false;

    // matchedDeals has already scoped this to the deals this person is on.
    return true;
  });

  const closedDealsYTD = matchedDeals.filter((d) => {
    if (!d.closingdate) return false;

    const dealYear = new Date(d.closingdate).getFullYear();
    if (dealYear !== selectedYear) return false;

    return d.dealStage?.toLowerCase() === "closed";
  });

  // ── THE DEALS BEHIND THE DOLLAR TILES ──
  // Every commission tile below is the sum of one of these lists and nothing
  // else, and clicking the tile opens that same list, so the rows in the
  // drawer always add back up to the figure that opened it.
  //
  // A deal credited to this person that paid him nothing carries no dollars,
  // so it belongs on the unit tiles and not in a dollars list: it would sit in
  // the drawer as a $0.00 row contributing nothing to the total above it.
  const closedPaidDealsYTD = closedDealsYTD.filter(
    (d) => commissionFor(d) !== 0,
  );
  const pendingPaidDealsYTD = pendingDealsYTD.filter(
    (d) => commissionFor(d) !== 0,
  );

  const closedCommissionTotal = closedPaidDealsYTD.reduce(
    (sum, d) => sum + commissionFor(d),
    0,
  );

  const pendingCommissionTotal = pendingPaidDealsYTD.reduce(
    (sum, d) => sum + commissionFor(d),
    0,
  );

  const netCommissionDealsYTD = [
    ...closedPaidDealsYTD,
    ...pendingPaidDealsYTD,
  ];

  const netCommissionTotal = netCommissionDealsYTD.reduce(
    (sum, d) => sum + commissionFor(d),
    0,
  );

  // Two axes on one screen, and each tile has to say which one it is on.
  //   UNITS + VOLUME -> the deals CREDITED to this person. One deal, one owner.
  //   COMMISSION     -> the deals that PAID this person, teammates' deals
  //                     included, each row his own slice.
  const UNITS_NOTE = `Deals credited to ${firstname} — one deal, one owner. Click to see them. The commission tiles are a different list: every deal that PAID ${firstname}, teammates' deals included, so the two counts are not the same set.`;
  const PAID_NOTE = `Every dollar ${firstname} was paid, including slices earned on deals credited to a teammate. Click to see those deals; the rows add up to this figure. The volume and deal-count tiles cover only the deals credited to ${firstname}.`;

  const monthNames = [
    "",
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const selectedMonthData =
    selectedMonth &&
    monthlyData.find(
      (m) => m.month === selectedMonth.month && m.year === selectedMonth.year,
    );

  return (
    <div className="">
      <section
        class={`flex flex-wrap gap-x-[2%] gap-y-5 my-10 ${isWidth ? "w-[96%] mx-auto" : ""}`}
      >
        <div
          class="w-full lg:w-[32%] group relative bg-white px-7 py-5 rounded-2xl border border-gray-200 shadow-sm
          transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
          overflow-hidden hover:-translate-y-[5px] hover:shadow-lg hover:border-[#1E293B] cursor-pointer"
          onClick={() => {
            setstatspopup(true);
            setselectedstat("Deals - Closed Volume");
            setstatsdata(closedDealsCount);
          }}
        >
          <span
            class="absolute top-0 left-0 h-1 w-full bg-gradient-to-r from-[#0f172a] to-[#1E293B]
            transform -translate-x-full transition-transform duration-500 ease-in-out
            group-hover:translate-x-0"
          ></span>

          <div class="flex justify-between items-center mb-2">
            <div
              class="w-14 h-14 flex items-center justify-center rounded-2xl
              bg-gradient-to-br from-blue-50 to-blue-100 text-blue-500 text-3xl
              transition-transform duration-300 ease-in-out group-hover:rotate-6 group-hover:scale-110"
            >
              💰
            </div>
          </div>

          <div
            id="totalSalesVolume"
            class="text-4xl font-bold mb-2 bg-gradient-to-r from-[#0f172a] to-[#1E293B]
            bg-clip-text text-transparent"
          >
            {formatNumber(ytdVolume)}
          </div>
          <div class="text-gray-500 text-sm font-medium">YTD Volume</div>
        </div>

        <div
          class="w-full lg:w-[32%] group relative bg-white px-7 py-5 rounded-2xl border border-gray-200 shadow-sm
          transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
          overflow-hidden hover:-translate-y-[5px] hover:shadow-lg hover:border-green-500 cursor-pointer"
          onClick={() => {
            setstatspopup(true);
            setselectedstat("Deals - Closed");
            setstatsdata(closedDealsCount);
          }}
        >
          <span
            class="absolute top-0 left-0 h-1 w-full bg-gradient-to-r from-green-500 to-emerald-400
            transform -translate-x-full transition-transform duration-500 ease-in-out
            group-hover:translate-x-0"
          ></span>

          <div class="flex justify-between items-center mb-2">
            <div
              class="w-14 h-14 flex items-center justify-center rounded-2xl
              bg-gradient-to-br from-emerald-50 to-green-100 text-green-500 text-3xl
              transition-transform duration-300 ease-in-out group-hover:rotate-6 group-hover:scale-110"
            >
              👥
            </div>
          </div>

          <div
            id="grossCommission"
            class="text-4xl font-bold mb-2 bg-gradient-to-r from-gray-900 to-green-500
            bg-clip-text text-transparent"
          >
            {formatNumber(closedDealsCount.length)}
          </div>
          <div class="text-gray-500 text-sm font-medium">Closed Deals</div>
        </div>

        <div
          class="w-full lg:w-[32%] group relative bg-white px-7 py-5 rounded-2xl border border-gray-200 shadow-sm
          transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
          overflow-hidden hover:-translate-y-[5px] hover:shadow-lg hover:border-amber-500 cursor-pointer"
          onClick={() => {
            setstatspopup(true);
            setselectedstat("Deals - Closed Commission Volume");
            // The list this tile's figure is the sum of, so the drawer rows add
            // back up to the number that opened it.
            setstatsdata(closedPaidDealsYTD);
          }}
        >
          <span
            class="absolute top-0 left-0 h-1 w-full bg-gradient-to-r from-amber-500 to-orange-400
            transform -translate-x-full transition-transform duration-500 ease-in-out
            group-hover:translate-x-0"
          ></span>

          <div class="flex justify-between items-center mb-2">
            <div
              class="w-14 h-14 flex items-center justify-center rounded-2xl
              bg-gradient-to-br from-orange-50 to-amber-100 text-orange-500 text-3xl
              transition-transform duration-300 ease-in-out group-hover:rotate-6 group-hover:scale-110"
            >
              💰
            </div>
          </div>

          <div
            id="brokerShare"
            class="text-4xl font-bold mb-2 bg-gradient-to-r from-gray-900 to-amber-500
            bg-clip-text text-transparent"
          >
            ${formatNumber(closedCommissionTotal)}
          </div>
          <div class="text-gray-500 text-sm font-medium">
            Closed Commission YTD
          </div>
        </div>
        <div
          class="w-full lg:w-[32%] group relative bg-white px-7 py-5 rounded-2xl border border-gray-200 shadow-sm
          transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
          overflow-hidden hover:-translate-y-[5px] hover:shadow-lg hover:border-amber-500 cursor-pointer"
          onClick={() => {
            setstatspopup(true);
            setselectedstat("Deals - Pending Commission Volume");
            setstatsdata(pendingDealsYTD);
          }}
        >
          <span
            class="absolute top-0 left-0 h-1 w-full bg-gradient-to-r from-amber-500 to-orange-400
            transform -translate-x-full transition-transform duration-500 ease-in-out
            group-hover:translate-x-0"
          ></span>

          <div class="flex justify-between items-center mb-2">
            <div
              class="w-14 h-14 flex items-center justify-center rounded-2xl
              bg-gradient-to-br from-orange-50 to-amber-100 text-orange-500 text-3xl
              transition-transform duration-300 ease-in-out group-hover:rotate-6 group-hover:scale-110"
            >
              💰
            </div>
          </div>

          <div
            id="brokerShare"
            class="text-4xl font-bold mb-2 bg-gradient-to-r from-gray-900 to-amber-500
            bg-clip-text text-transparent"
          >
            ${formatNumber(pendingCommissionTotal)}
          </div>
          <div class="text-gray-500 text-sm font-medium">
            Pending Commission YTD
          </div>
        </div>
        <div
          class="w-full lg:w-[32%] group relative bg-white px-7 py-5 rounded-2xl border border-gray-200 shadow-sm
          transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
          overflow-hidden hover:-translate-y-[5px] hover:shadow-lg hover:border-amber-500 cursor-pointer"
          onClick={() => {
            setstatspopup(true);
            setselectedstat("Deals - Closed Commission Volume");
            setstatsdata(netCommissionDealsYTD);
          }}
        >
          <span
            class="absolute top-0 left-0 h-1 w-full bg-gradient-to-r from-amber-500 to-orange-400
            transform -translate-x-full transition-transform duration-500 ease-in-out
            group-hover:translate-x-0"
          ></span>

          <div class="flex justify-between items-center mb-2">
            <div
              class="w-14 h-14 flex items-center justify-center rounded-2xl
              bg-gradient-to-br from-orange-50 to-amber-100 text-orange-500 text-3xl
              transition-transform duration-300 ease-in-out group-hover:rotate-6 group-hover:scale-110"
            >
              💰
            </div>
          </div>

          <div
            id="brokerShare"
            class="text-4xl font-bold mb-2 bg-gradient-to-r from-gray-900 to-amber-500
            bg-clip-text text-transparent"
          >
            ${formatNumber(netCommissionTotal)}
          </div>
          <div class="text-gray-500 text-sm font-medium">
            Net Commission YTD
          </div>
        </div>
      </section>

      {/* YEARLY BAR CHART */}
      <div
        className={`bg-white shadow rounded-xl p-6 ${isWidth ? "w-[96%] mx-auto" : ""}`}
      >
        <div className="flex flex-wrap justify-between items-center mb-3">
          <h3 className="text-xl font-semibold mb-4">Yearly Performance</h3>
          <div className="relative w-full sm:w-auto">
            <button
              onClick={() => setYearDropdownVisible(!yearDropdownVisible)}
              className="border border-gray-300 rounded-md px-3 py-1 text-sm w-full sm:w-auto text-left bg-white"
            >
              {filterYear}
            </button>

            <YearFilterDropdown
              visible={yearDropdownVisible}
              onSelect={(year) => {
                setFilterYear(year);
                setSelectedYear(Number(year)); // 👈 performance logic sync
                setYearDropdownVisible(false);
              }}
              activateOutsideClick={() => setYearDropdownVisible(false)}
              style={{ top: "40px", right: "0px" }}
            />
          </div>
        </div>

        <div className="flex items-end gap-2">
          {monthlyData.map((m, i) => {
            const maxNetValue = Math.max(
              ...monthlyData.map((d) => d.netCommission),
              1,
            );
            const barHeight = Math.max(
              10,
              (m.netCommission / maxNetValue) * 160,
            );

            const isSelected =
              selectedMonth?.month === m.month &&
              selectedMonth?.year === m.year;

            return (
              <div
                key={i}
                className="flex-1 flex flex-col items-center cursor-pointer transition-all"
                onClick={() =>
                  setSelectedMonth(
                    isSelected ? null : { month: m.month, year: m.year },
                  )
                }
              >
                <div
                  className={`w-full rounded-t-md transition-all ${
                    isSelected
                      ? "bg-[#1E293B]"
                      : "bg-slate-300 hover:bg-[#1E293B]"
                  }`}
                  style={{ height: `${barHeight}px` }}
                ></div>
                <p
                  className={`text-xs mt-2 ${
                    isSelected
                      ? "text-[#1E293B] font-semibold"
                      : "text-gray-500"
                  }`}
                >
                  {monthNames[m.month]}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* SELECTED MONTH DETAILS */}
      {selectedMonthData && (
        <div
          className={`bg-white shadow rounded-xl p-6 my-8 ${isWidth ? "w-[96%] mx-auto" : ""}`}
        >
          <h3 className="text-xl font-semibold mb-3">
            {monthNames[selectedMonthData.month]} {selectedMonthData.year}{" "}
            Summary
          </h3>

          <div className="flex flex-wrap justify-between mt-5 gap-y-8">
            <div className="w-full lg:w-[23%]">
              <p className="text-gray-500">Closed Deals</p>
              <h2 className="text-3xl font-bold mt-1 text-gray-800">
                {formatNumber(selectedMonthData.closedDealsCount)}
              </h2>
            </div>
            <div className="w-full lg:w-[23%]">
              <p className="text-gray-500">Closed Commission</p>
              <h2 className="text-3xl font-bold mt-1 text-[#10B981]">
                ${formatNumber(selectedMonthData.closedCommission)}
              </h2>
            </div>

            <div className="w-full lg:w-[23%]">
              <p className="text-gray-500">Pending Commission</p>
              <h2 className="text-3xl font-bold mt-1 text-[#F59E0B]">
                ${formatNumber(selectedMonthData.pendingCommission)}
              </h2>
            </div>

            <div className="w-full lg:w-[23%]">
              <p className="text-gray-500">Net Commission</p>
              <h2 className="text-3xl font-bold mt-1 text-gray-800">
                ${formatNumber(selectedMonthData.netCommission)}
              </h2>
            </div>
          </div>
        </div>
      )}
      {statspopup && (
        <StatDetails
          // isLoading={isLoading}
          Data={statsdata}
          selectedstat={selectedstat}
          onClose={() => setstatspopup(false)}
          isStatCard={true}
          statUserId={id}
          role={role}
          onSubmit={(data) => {
            console.log(data);
          }}
        />
      )}
    </div>
  );
}

export default TeamPerformanceStats;
